import { Page } from '../components/Page.js';
import {
  CASE,
  FLAGS,
  PROVENANCE,
  CITATION_KEY,
  ORDERS_FRAMEWORK,
  SOURCES,
  NOTIFY_DUTIES,
  PROHIBITIONS,
  EMPLOYMENT,
  EMPLOYMENT_INTRO,
  EMPLOYMENT_KEY_DISTINCTION,
  EXEMPTION_NARROW,
  WORK_CAN,
  WORK_CANNOT,
  WORK_SCENARIOS,
  GREY_AREA,
  DEVICE_INTRO,
  DEVICE_RULES,
  DEVICE_CALLOUT,
  CONSEQUENCES,
  KEY_DATES,
  type Cite,
} from '../content/conditions.js';

function Cites({ cites }: { cites: Cite[] }) {
  return (
    <span className="cite">
      {cites.map((c, i) => (
        <span key={c.label}>
          {i > 0 && ' · '}
          {c.url ? (
            <a href={c.url} target="_blank" rel="noreferrer noopener">{c.label}</a>
          ) : (
            <span>{c.label}</span>
          )}
        </span>
      ))}
    </span>
  );
}

export function Conditions() {
  return (
    <Page
      eyebrow="Compliance"
      title="My conditions"
      actions={<span className="pill pill-ok"><span className="dot dot-ok" />Verified to primary law · 25 Jun 2026</span>}
    >
      <div className="doc">
        <p className="lead">
          A plain-English breakdown of what you must do and must not do, with the law behind each
          point so you can refer to it and show others. This is <strong>legal information, not legal
          advice</strong> — confirm anything specific with your offender manager, the police Public
          Protection Unit (PPU), and a solicitor.
        </p>

        <div className="callout">
          <div className="c-title">Legal basis &amp; how to read this</div>
          <p>{PROVENANCE}</p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.55 }}>
            {CITATION_KEY.map((k) => (
              <li key={k.marker}><strong className="mono">{k.marker}</strong> — {k.text}</li>
            ))}
          </ul>
        </div>

        {/* Case summary */}
        <h2>Your case</h2>
        <dl className="kv">
          {CASE.map(([k, v]) => (
            <div key={k} style={{ display: 'contents' }}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>

        {/* Legal basis of the orders */}
        <h2>The legal basis of your orders</h2>
        {ORDERS_FRAMEWORK.map((d) => (
          <div className="cond" key={d.title}>
            <div className="cond-head">
              <div style={{ flex: 1 }}>
                <div className="cond-title">{d.title}</div>
                <div className="cond-plain">{d.plain}</div>
              </div>
            </div>
            <div className="cond-foot"><Cites cites={d.cites} /></div>
          </div>
        ))}

        {/* Priority flags */}
        {FLAGS.map((f) => (
          <div className={`callout ${f.kind}`} key={f.title}>
            <div className="c-title">⚠ {f.title}</div>
            <p>{f.body}</p>
          </div>
        ))}

        {/* Notification duties */}
        <h2>What you must do — reporting duties ("the register")</h2>
        <p className="lead" style={{ fontSize: 13.5 }}>
          These are reporting duties under the Sexual Offences Act 2003. They run for your whole
          10-year notification period. All time limits are calendar days.
        </p>
        {NOTIFY_DUTIES.map((d) => (
          <div className="cond" key={d.title}>
            <div className="cond-head">
              <div style={{ flex: 1 }}>
                <div className="cond-title">{d.title}</div>
                <div className="cond-plain">{d.plain}</div>
              </div>
            </div>
            <div className="cond-foot">
              {d.deadline && <span className="badge badge-warn">{d.deadline}</span>}
              {d.tag && <span className="tag-duty">{d.tag}</span>}
              <Cites cites={d.cites} />
            </div>
          </div>
        ))}

        {/* SHPO prohibitions */}
        <h2>What you must not do — your SHPO (12 prohibitions)</h2>
        <p className="lead" style={{ fontSize: 13.5 }}>
          Your Sexual Harm Prevention Order runs for 10 years (made 19 Dec 2022). Below is each
          prohibition from the order in plain English. The sealed order text always governs — if any
          wording here differs, follow the order and check with your PPU.
        </p>
        {PROHIBITIONS.map((p) => (
          <div className="cond" key={p.n}>
            <div className="cond-head">
              <div className="cond-num">{p.n}</div>
              <div style={{ flex: 1 }}>
                <div className="cond-title">{p.title}</div>
                <div className="cond-plain">{p.plain}</div>
              </div>
            </div>
            <div className="cond-foot">
              {p.duty && <span className="tag-duty">{p.duty}</span>}
              <Cites cites={p.cites} />
            </div>
          </div>
        ))}

        {/* Employment & work devices */}
        <h2>Employment &amp; work devices</h2>
        <p className="lead" style={{ fontSize: 13.5 }}>{EMPLOYMENT_INTRO}</p>
        <div className="callout warn">
          <div className="c-title">⚠ {EMPLOYMENT_KEY_DISTINCTION.title}</div>
          <p>{EMPLOYMENT_KEY_DISTINCTION.body}</p>
        </div>
        {EMPLOYMENT.map((d) => (
          <div className="cond" key={d.title}>
            <div className="cond-head">
              <div style={{ flex: 1 }}>
                <div className="cond-title">{d.title}</div>
                <div className="cond-plain">{d.plain}</div>
              </div>
            </div>
            <div className="cond-foot">
              {d.deadline && <span className="badge badge-warn">{d.deadline}</span>}
              {d.tag && <span className="tag-duty">{d.tag}</span>}
              <Cites cites={d.cites} />
            </div>
          </div>
        ))}

        {/* What the work-device exemption covers */}
        <h3 style={{ fontSize: 15, marginTop: 26 }}>What the work-device exemption covers — and what it doesn’t</h3>
        <div className="callout bad">
          <div className="c-title">⚠ {EXEMPTION_NARROW.title}</div>
          <p>{EXEMPTION_NARROW.body}</p>
        </div>
        <div className="cancan">
          <div className="canbox no">
            <h4>On a work device you STILL cannot</h4>
            <ul>{WORK_CANNOT.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
          <div className="canbox yes">
            <h4>On a work device you can</h4>
            <ul>{WORK_CAN.map((t) => <li key={t}>{t}</li>)}</ul>
          </div>
        </div>
        {WORK_SCENARIOS.map((d) => (
          <div className="cond" key={d.title}>
            <div className="cond-head">
              <div style={{ flex: 1 }}>
                <div className="cond-title">{d.title}</div>
                <div className="cond-plain">{d.plain}</div>
              </div>
            </div>
            <div className="cond-foot">
              {d.deadline && <span className="badge badge-warn">{d.deadline}</span>}
              {d.tag && <span className="tag-duty">{d.tag}</span>}
              <Cites cites={d.cites} />
            </div>
          </div>
        ))}
        <div className="callout warn">
          <div className="c-title">⚠ {GREY_AREA.title}</div>
          <p>{GREY_AREA.body}</p>
        </div>

        {/* What you must tell police about a device */}
        <h2>What you must tell the police about a device</h2>
        <p className="lead" style={{ fontSize: 13.5 }}>{DEVICE_INTRO}</p>
        {DEVICE_RULES.map((d) => (
          <div className="cond" key={d.title}>
            <div className="cond-head">
              <div style={{ flex: 1 }}>
                <div className="cond-title">{d.title}</div>
                <div className="cond-plain">{d.plain}</div>
              </div>
            </div>
            <div className="cond-foot">
              {d.deadline && <span className="badge badge-warn">{d.deadline}</span>}
              {d.tag && <span className="tag-duty">{d.tag}</span>}
              <Cites cites={d.cites} />
            </div>
          </div>
        ))}
        <div className="callout warn">
          <div className="c-title">⚠ {DEVICE_CALLOUT.title}</div>
          <p>{DEVICE_CALLOUT.body}</p>
        </div>

        {/* Consequences */}
        <h2>If you get it wrong</h2>
        {CONSEQUENCES.map((c) => (
          <div className="callout bad" key={c.title}>
            <div className="c-title">{c.title}</div>
            <p>{c.plain}</p>
            <p style={{ marginTop: 8 }}><Cites cites={c.cites} /></p>
          </div>
        ))}
        <div className="callout">
          <div className="c-title">The golden rule</div>
          <p>If you are unsure whether something is notifiable or allowed, ask your PPU / offender
            manager (and a solicitor) <strong>before</strong> acting or before the deadline — never
            guess. Keep proof of everything (that is what this system is for).</p>
        </div>

        {/* Key dates */}
        <h2>Key dates</h2>
        <dl className="kv">
          {KEY_DATES.map(([d, what]) => (
            <div key={what} style={{ display: 'contents' }}>
              <dt className="mono">{d}</dt>
              <dd>{what}</dd>
            </div>
          ))}
        </dl>

        {/* Sources */}
        <h2>Sources &amp; legal basis</h2>
        {SOURCES.map((g) => (
          <div key={g.group} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, margin: '8px 0 4px' }}>{g.group}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--ink2)', lineHeight: 1.7 }}>
              {g.items.map((c) => (
                <li key={c.label}>
                  {c.url ? (
                    <a className="cite" href={c.url} target="_blank" rel="noreferrer noopener">{c.label}</a>
                  ) : (
                    c.label
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <p className="disclaimer">
          Compiled from primary legislation (legislation.gov.uk) and official GOV.UK guidance, and
          adversarially verified against the primary sources; current to 25 June 2026. Law changes —
          re-verify before relying on this for any legal step. This page does not constitute legal
          advice. The full cited reference lives in the project as
          <span className="mono"> docs/COMPLIANCE-SOURCE-OF-TRUTH.md</span>.
        </p>
      </div>
    </Page>
  );
}
